import React, { useMemo } from 'react';

import { finleyAssert } from '@finley-technologies/common';

import {
  TaskFragmentGQLType,
  useGetCurrentCreditAgreementFullArtifactQuery,
  useGetInvoiceByIdQuery,
  useGetInvoiceDisbursementByIdQuery,
  useGetInvoicePaymentInKindItemByIdQuery,
  useGetResolvedLoanByIdQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { InvoiceDisbursementDrawer } from 'packages/invoices/ui/disbursements/InvoiceDisbursementDrawer';
import { InvoiceDetailSideDrawer } from 'packages/invoices/ui/invoiceDetail/InvoiceDetailSideDrawer';
import { InvoicePaymentInKindItemDrawer } from 'packages/invoices/ui/paymentInKind/InvoicePaymentInKindItemDrawer';
import { FundingRequestDrawer } from 'packages/loans/ui/FundingRequestDrawer';
import { LenderSharesDrawer } from 'packages/loans/ui/LenderSharesDrawer';

const FundingRequestTaskDrawer = ({
  fundingRequestId,
  loanId,
  onClose,
}: {
  fundingRequestId: string;
  loanId: string;
  onClose: () => void;
}) => {
  const useGetLoanQuery = useGetResolvedLoanByIdQuery({
    variables: { loanId },
  });

  if (useGetLoanQuery.error) {
    throw new Error(
      `Failed to get Loan for Funding Request Task. Please contact Finley support.`,
    );
  }

  if (!useGetLoanQuery.data?.loan) {
    return null;
  }

  return (
    <FundingRequestDrawer
      isOpen
      fundingRequestId={fundingRequestId}
      loan={useGetLoanQuery.data.loan}
      onToggle={onClose}
    />
  );
};

const InvoiceTaskDrawer = ({
  invoiceId,
  onClose,
}: {
  invoiceId: string;
  onClose: () => void;
}) => {
  const useGetInvoiceQuery = useGetInvoiceByIdQuery({
    variables: { invoiceId },
  });

  if (useGetInvoiceQuery.error) {
    throw new Error(
      `Failed to get Invoice for Task. Please contact Finley support.`,
    );
  }

  if (!useGetInvoiceQuery.data?.getInvoiceById) {
    return null;
  }

  return (
    <InvoiceDetailSideDrawer
      isOpen
      invoice={useGetInvoiceQuery.data.getInvoiceById}
      onClose={onClose}
    />
  );
};

const InvoiceDisbursementTaskDrawer = ({
  invoiceDisbursementId,
  creditFacilityId,
  onClose,
}: {
  invoiceDisbursementId: string;
  creditFacilityId: string;
  onClose: () => void;
}) => {
  const useGetInvoiceDisbursementQuery = useGetInvoiceDisbursementByIdQuery({
    variables: { invoiceDisbursementId },
  });

  if (useGetInvoiceDisbursementQuery.error) {
    throw new Error(
      `Failed to get Invoice Disbursement for Funding Disbursement Task. Please contact Finley support.`,
    );
  }

  const currentCreditAgreementQuery = useGetCurrentCreditAgreementFullArtifactQuery(
    {
      variables: {
        creditFacilityId,
      },
    },
  );

  if (currentCreditAgreementQuery.error) {
    throw new Error(
      `Failed to get Credit Agreement for Tasks. Please contact Finley support.`,
    );
  }

  const facilityMechanics = useMemo(() => {
    if (
      currentCreditAgreementQuery.data &&
      currentCreditAgreementQuery.data.currentCreditAgreementFullArtifact
    ) {
      const creditAgreementString =
        currentCreditAgreementQuery.data.currentCreditAgreementFullArtifact
          .creditAgreement;

      const parsedCreditAgreement = parseCreditAgreementFullArtifact(
        creditAgreementString,
      );

      finleyAssert(
        parsedCreditAgreement,
        `Expect parsed currentCreditAgreementFullArtifact to exist for [creditFacilityId ${creditFacilityId}]`,
      );

      return parsedCreditAgreement.facilityMechanics;
    }
    return null;
  }, [creditFacilityId, currentCreditAgreementQuery.data]);

  if (
    !useGetInvoiceDisbursementQuery.data?.getInvoiceDisbursementById ||
    !facilityMechanics
  ) {
    return null;
  }

  return (
    <InvoiceDisbursementDrawer
      isOpen
      invoiceDisbursement={
        useGetInvoiceDisbursementQuery.data.getInvoiceDisbursementById
      }
      facilityMechanics={facilityMechanics}
      onToggle={onClose}
    />
  );
};

const InvoicePaymentInKindTaskDrawer = ({
  invoicePaymentInKindItemId,
  creditFacilityId,
  onClose,
}: {
  invoicePaymentInKindItemId: string;
  creditFacilityId: string;
  onClose: () => void;
}) => {
  const useGetInvoicePaymentInKindItemQuery = useGetInvoicePaymentInKindItemByIdQuery(
    {
      variables: { invoicePaymentInKindItemId },
    },
  );

  if (useGetInvoicePaymentInKindItemQuery.error) {
    throw new Error(
      `Failed to get Invoice Payment In Kind for Task. Please contact Finley support.`,
    );
  }

  const currentCreditAgreementQuery = useGetCurrentCreditAgreementFullArtifactQuery(
    {
      variables: {
        creditFacilityId,
      },
    },
  );

  if (currentCreditAgreementQuery.error) {
    throw new Error(
      `Failed to get Credit Agreement for Tasks. Please contact Finley support.`,
    );
  }

  const facilityMechanics = useMemo(() => {
    if (
      currentCreditAgreementQuery.data &&
      currentCreditAgreementQuery.data.currentCreditAgreementFullArtifact
    ) {
      const creditAgreementString =
        currentCreditAgreementQuery.data.currentCreditAgreementFullArtifact
          .creditAgreement;

      const parsedCreditAgreement = parseCreditAgreementFullArtifact(
        creditAgreementString,
      );

      finleyAssert(
        parsedCreditAgreement,
        `Expect parsed currentCreditAgreementFullArtifact to exist for [creditFacilityId ${creditFacilityId}]`,
      );

      return parsedCreditAgreement.facilityMechanics;
    }
    return null;
  }, [creditFacilityId, currentCreditAgreementQuery.data]);

  if (
    !useGetInvoicePaymentInKindItemQuery.data
      ?.getInvoicePaymentInKindItemById ||
    !facilityMechanics
  ) {
    return null;
  }

  return (
    <InvoicePaymentInKindItemDrawer
      isOpen
      invoicePaymentInKindItem={
        useGetInvoicePaymentInKindItemQuery.data.getInvoicePaymentInKindItemById
      }
      facilityMechanics={facilityMechanics}
      onToggle={onClose}
    />
  );
};

const LenderSharesTaskDrawer = ({
  loanId,
  fundingComponentId,
  creditFacilityId,
  lenderShareId,
  onClose,
}: {
  loanId: string;
  fundingComponentId: string;
  creditFacilityId: string;
  lenderShareId: string;
  onClose: () => void;
}) => {
  const useGetLoanQuery = useGetResolvedLoanByIdQuery({
    variables: { loanId },
  });

  if (useGetLoanQuery.error) {
    throw new Error(
      `Failed to get Loan for Lender Shares Task. Please contact Finley support.`,
    );
  }

  const currentCreditAgreementQuery = useGetCurrentCreditAgreementFullArtifactQuery(
    {
      variables: {
        creditFacilityId,
      },
    },
  );

  if (currentCreditAgreementQuery.error) {
    throw new Error(
      `Failed to get Credit Agreement for Tasks. Please contact Finley support.`,
    );
  }

  const facilityMechanics = useMemo(() => {
    if (
      currentCreditAgreementQuery.data &&
      currentCreditAgreementQuery.data.currentCreditAgreementFullArtifact
    ) {
      const creditAgreementString =
        currentCreditAgreementQuery.data.currentCreditAgreementFullArtifact
          .creditAgreement;

      const parsedCreditAgreement = parseCreditAgreementFullArtifact(
        creditAgreementString,
      );

      finleyAssert(
        parsedCreditAgreement,
        `Expect parsed currentCreditAgreementFullArtifact to exist for [creditFacilityId ${creditFacilityId}]`,
      );

      return parsedCreditAgreement.facilityMechanics;
    }
    return null;
  }, [creditFacilityId, currentCreditAgreementQuery.data]);

  if (!useGetLoanQuery.data?.loan || !facilityMechanics) {
    return null;
  }

  return (
    <LenderSharesDrawer
      isOpen
      loan={useGetLoanQuery.data.loan}
      lenderShareId={lenderShareId}
      fundingComponentId={fundingComponentId}
      facilityMechanics={facilityMechanics}
      onToggle={onClose}
    />
  );
};

export const TaskDetailDrawer = ({
  selectedTask,
  onClose,
}: {
  selectedTask: TaskFragmentGQLType;
  onClose: () => void;
}) => {
  switch (selectedTask.domainProperties.categorySpecificProperties.__typename) {
    case 'FundingRequestTaskSpecificProperties': {
      const loanId =
        selectedTask.domainProperties.categorySpecificProperties
          .fundingRequestLoanId;
      const fundingRequestId =
        selectedTask.domainProperties.categorySpecificProperties
          .fundingRequestId;

      if (!loanId) {
        return <></>;
      }

      return (
        <FundingRequestTaskDrawer
          fundingRequestId={fundingRequestId}
          loanId={loanId}
          onClose={onClose}
        />
      );
    }
    case 'InvoiceTaskSpecificProperties': {
      const invoiceId =
        selectedTask.domainProperties.categorySpecificProperties.invoiceId;

      return <InvoiceTaskDrawer invoiceId={invoiceId} onClose={onClose} />;
    }
    case 'LenderSharesTaskSpecificProperties': {
      const loanId =
        selectedTask.domainProperties.categorySpecificProperties
          .lenderSharesLoanId;
      const lenderShareId =
        selectedTask.domainProperties.categorySpecificProperties.lenderSharesId;
      const fundingRequestComponentId =
        selectedTask.domainProperties.categorySpecificProperties
          .fundingComponentId;

      return (
        <LenderSharesTaskDrawer
          loanId={loanId}
          fundingComponentId={fundingRequestComponentId}
          lenderShareId={lenderShareId}
          creditFacilityId={selectedTask.domainProperties.creditFacility.id}
          onClose={onClose}
        />
      );
    }
    case 'FundingDisbursementTaskSpecificProperties': {
      const invoiceDisbursementId =
        selectedTask.domainProperties.categorySpecificProperties
          .fundingDisbursementId;

      return (
        <InvoiceDisbursementTaskDrawer
          invoiceDisbursementId={invoiceDisbursementId}
          creditFacilityId={selectedTask.domainProperties.creditFacility.id}
          onClose={onClose}
        />
      );
    }
    case 'PaymentInKindItemTaskSpecificProperties': {
      const invoicePaymentInKindItemId =
        selectedTask.domainProperties.categorySpecificProperties
          .paymentInKindItemId;
      return (
        <InvoicePaymentInKindTaskDrawer
          invoicePaymentInKindItemId={invoicePaymentInKindItemId}
          creditFacilityId={selectedTask.domainProperties.creditFacility.id}
          onClose={onClose}
        />
      );
    }
    default: {
      throw Error(
        '[TaskDetailDrawer]: categorySpecificProperties.__typename should never be undefined',
      );
    }
  }
};
